import React, { useState, useEffect } from "react";
import { Box, IconButton, Stack, TextField, Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { formatLatLng } from "@/utils/utils";

/**
 * 座標セルコンポーネント
 * 緯度経度を表示し、ホバー時に編集アイコンを表示して編集モードに切り替え
 *
 * @param latitude - 緯度
 * @param longitude - 経度
 * @param rowId - 行ID
 * @param onEdit - 編集完了時のコールバック関数
 */
export interface CoordinatesCellProps {
  latitude: number | null;
  longitude: number | null;
  rowId: number;
  onEdit: (latitude: number | null, longitude: number | null) => void;
  disabled?: boolean;
  onEditingChange?: (rowId: number, field: string, isEditing: boolean) => void;
}

export const CoordinatesCell: React.FC<CoordinatesCellProps> = ({
  latitude,
  longitude,
  rowId,
  onEdit,
  disabled = false,
  onEditingChange,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editLatitude, setEditLatitude] = useState(latitude?.toString() ?? "");
  const [editLongitude, setEditLongitude] = useState(
    longitude?.toString() ?? ""
  );

  // latitude/longitudeプロップが変更されたら編集値を更新
  useEffect(() => {
    if (!isEditing) {
      setEditLatitude(latitude?.toString() ?? "");
      setEditLongitude(longitude?.toString() ?? "");
    }
  }, [latitude, longitude, isEditing]);

  /**
   * 半角数字とドット(.)のみを許可する入力フィルタ
   */
  const filterNumericInput = (value: string): string => {
    // 半角数字とドット、マイナス記号のみを許可
    return value.replace(/[^0-9.-]/g, "");
  };

  /**
   * 編集内容を保存する
   */
  const handleSave = (): void => {
    setIsEditing(false);
    onEditingChange?.(rowId, "coordinates", false);

    // 空文字の場合はnullとして扱う（削除）
    const newLatitude =
      editLatitude.trim() === "" ? null : parseFloat(editLatitude);
    const newLongitude =
      editLongitude.trim() === "" ? null : parseFloat(editLongitude);

    // 値が変更されている場合はコールバックを呼び出す
    // 片方だけ削除する場合や、両方削除する場合も含む
    if (
      newLatitude !== latitude ||
      newLongitude !== longitude ||
      newLatitude === null ||
      newLongitude === null
    ) {
      onEdit(newLatitude, newLongitude);
    }
  };

  /**
   * 編集をキャンセルする
   */
  const handleCancel = (): void => {
    setEditLatitude(latitude?.toString() ?? "");
    setEditLongitude(longitude?.toString() ?? "");
    setIsEditing(false);
    onEditingChange?.(rowId, "coordinates", false);
  };

  /**
   * 編集モード開始
   */
  const handleStartEdit = (): void => {
    setIsEditing(true);
    onEditingChange?.(rowId, "coordinates", true);
  };

  if (isEditing) {
    return (
      <Box sx={{ width: "100%", py: 1, px: 1 }}>
        <Stack spacing={1}>
          <TextField
            label="緯度"
            value={editLatitude}
            onChange={(e) => {
              setEditLatitude(filterNumericInput(e.target.value));
            }}
            onKeyDown={(e) => {
              // 矢印キーの場合、イベントの伝播を止める(テキスト入力フォーム内でのカーソル移動を可能にする)
              if (
                ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(
                  e.key
                )
              ) {
                e.stopPropagation();
              }
            }}
            type="number"
            size="small"
            autoFocus
            fullWidth
            placeholder="例: 35.681236"
            inputProps={{ step: "any" }}
          />
          <TextField
            label="経度"
            value={editLongitude}
            onChange={(e) => {
              setEditLongitude(filterNumericInput(e.target.value));
            }}
            onKeyDown={(e) => {
              // 矢印キーの場合、イベントの伝播を止める(テキスト入力フォーム内でのカーソル移動を可能にする)
              if (
                ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(
                  e.key
                )
              ) {
                e.stopPropagation();
              }
            }}
            type="number"
            size="small"
            fullWidth
            placeholder="例: 139.767125"
            inputProps={{ step: "any" }}
          />
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ mt: 1 }}
        >
          <Button size="small" variant="outlined" onClick={handleCancel}>
            キャンセル
          </Button>
          <Button size="small" variant="contained" onClick={handleSave}>
            保存
          </Button>
        </Stack>
      </Box>
    );
  }

  // 表示用のテキストを生成（nullの場合は空文字列）
  const latText = formatLatLng(latitude);
  const lngText = formatLatLng(longitude);
  const displayText =
    latText && lngText ? `${latText}, ${lngText}` : latText || lngText || "";

  if (disabled) {
    return (
      <Box sx={{ textAlign: "left", width: "100%", py: 1 }}>{displayText}</Box>
    );
  }

  return (
    <Box
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        width: "100%",
        height: "100%",
        py: 1,
      }}
    >
      <Box>{displayText}</Box>
      {isHovered && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleStartEdit();
          }}
          aria-label="編集"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};
